import { Intent as BlueprintIntent } from '@blueprintjs/core';
import { WarningAlt } from '@carbon/icons-react';
import * as React from 'react';
import { useParams } from 'react-router-dom';
import {
  Body,
  Button,
  Icon,
  Input,
  LoadingIcon,
  Modal,
} from 'vet-bones/design-system/components';

import { useAppDispatch, useAppSelector } from 'src/ui/app/hooks';
import {
  DELETE_ORGANIZATION_DIALOG_CANCEL,
  DELETE_ORGANIZATION_DIALOG_INPUT,
  DELETE_ORGANIZATION_DIALOG_SUBMIT,
} from 'src/ui/constants/testIds';
import { queueMessage, queueNotification } from 'src/ui/features/notifications';
import { DialogType, closedDialog } from 'src/ui/features/ui';
import { graphQLClient } from 'src/ui/graph/graphQLClient';
import { useDeleteOrganizationMutation } from 'src/ui/graph/types';
import { useOnError, useOnMutate } from 'src/ui/hooks/graph';
import { useInvalidateOrganization } from 'src/ui/hooks/organization';
import { isDialogOpenSelector } from 'src/ui/selectors/isDialogOpen';
import * as copy from 'src/ui/templates/copy';

const DELETE_CONFIRMATION = 'DELETE';

const isOpenSelector = isDialogOpenSelector(DialogType.DELETE_ORGANIZATION);

export const DeleteOrganizationDialog: React.VFC = () => {
  const dispatch = useAppDispatch();
  const { org = '' } = useParams<{ org?: string }>();

  const invalidateOrg = useInvalidateOrganization(org);

  const isOpen = useAppSelector(isOpenSelector);
  const onError = useOnError();
  const onMutate = useOnMutate();

  const [value, setValue] = React.useState('');

  const {
    isLoading,
    mutate: deleteOrganization,
  } = useDeleteOrganizationMutation(graphQLClient, {
    onMutate,
    onError,
    onSuccess: (data) => {
      invalidateOrg();

      if (!data.deleteOrganization?.success) {
        dispatch(
          queueNotification(
            data.deleteOrganization?.error ||
              copy.components.organization.deleteDialog.notifications.error
          )
        );
        return;
      }

      dispatch(
        queueMessage({
          intent: BlueprintIntent.SUCCESS,
          message:
            copy.components.organization.deleteDialog.notifications.success,
        })
      );
      handleClose();
    },
  });

  const handleClose = () => {
    dispatch(closedDialog());
    setValue('');
  };

  const handleSubmit = () => {
    if (value.trim() !== DELETE_CONFIRMATION || isLoading) {
      return;
    }

    deleteOrganization({ org_domain: org });
  };

  const isSubmitDisabled = value.trim() !== DELETE_CONFIRMATION || isLoading;

  return (
    <Modal
      canOutsideClickClose={false}
      className="organization-dialog"
      footerActions={
        <>
          <Button
            onClick={handleClose}
            size="sm"
            testId={DELETE_ORGANIZATION_DIALOG_CANCEL}
            variant="ghost-gray"
          >
            {copy.components.organization.deleteDialog.buttons.cancel}
          </Button>
          <Button
            disabled={isSubmitDisabled}
            leftIcon={isLoading ? LoadingIcon : undefined}
            onClick={handleSubmit}
            size="sm"
            testId={DELETE_ORGANIZATION_DIALOG_SUBMIT}
            variant="primary-gray"
          >
            {copy.components.organization.deleteDialog.buttons.submit}
          </Button>
        </>
      }
      headerSize="lg"
      isOpen={isOpen}
      onClose={handleClose}
      title={copy.components.organization.deleteDialog.title}
    >
      <div className="organization-dialog-body">
        <div className="organization-dialog-warning">
          <Icon IconComponent={WarningAlt} size="lg" />
          <Body size="md">
            {copy.components.organization.deleteDialog.warning.pre}
            <strong>
              {copy.components.organization.deleteDialog.warning.strong}
            </strong>
            {copy.components.organization.deleteDialog.warning.post}
          </Body>
        </div>

        <Input
          autoFocus
          clearable={false}
          label={copy.components.organization.dialog.inputs.delete.label}
          onChange={(e) => setValue(e.target.value)}
          size="md"
          testId={DELETE_ORGANIZATION_DIALOG_INPUT}
          value={value}
        />
      </div>
    </Modal>
  );
};
